<template>
  <div class="w-full pb-10">
    <vx-card class="org-card w-full max-w-5xl mx-auto">
      <div class="p-2 md:p-4">
        
        <!-- Top Section: Logo & Basic Info -->
        <div class="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
          
          <!-- Left: Logo Upload -->
          <div class="w-full md:w-1/3 flex flex-col items-center pt-2">
            
            <div class="profile-picture-wrapper" @click="$refs.update_avatar_input.click()">
              <vs-avatar
                v-if="logoInput"
                class="profile-picture m-0"
                size="160px"
                :src="logoInput"
              />
              <vs-avatar
                v-else-if="localData.logo"
                class="profile-picture m-0"
                size="160px"
                :src="constants.s3Bucket + '/organization/logo/' + localData.logo"
              />
              <vs-avatar
                v-else
                class="profile-picture m-0"
                size="160px"
                :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
              />
              
              <!-- Persistent Camera Badge -->
              <div class="camera-badge pointer-events-none">
                <feather-icon icon="CameraIcon" svgClasses="w-5 h-5 text-gray-700" />
              </div>
            </div>

            <!-- Description to fill vertical gap -->
            <div class="text-center mt-5 px-4 max-w-[250px]">
              <p class="text-sm font-semibold text-gray-800 mb-1">Organization Logo</p>
              <p class="text-xs text-gray-500 leading-relaxed">
                Click to upload. We recommend a 1:1 aspect ratio.<br>
                Max size: 1MB (JPG, PNG)
              </p>
            </div>

            <!-- Hidden Input & Cropper Prompt -->
            <vs-prompt
              :title="$t('orgDashboard.createOrganization.logo.adjustTitle')"
              :accept-text="$t('orgDashboard.createOrganization.logo.save')"
              @cancel="closeEditLogoPrompt"
              @accept="cropAndUploadLogo"
              @close="closeEditLogoPrompt"
              :active.sync="editLogoPrompt"
            >
              <div class="con-exemple-prompt">
                <clipper-basic 
                  v-if="editLogoPrompt" 
                  :src="tempLogo" 
                  :ratio="aspectRatio" 
                  ref="clipper"
                ></clipper-basic>
              </div>
            </vs-prompt>
            <input
              type="file"
              class="hidden"
              name="Profile Picture"
              ref="update_avatar_input"
              @change="openEditLogoPrompt"
              accept="image/*"
            />
          </div>

          <!-- Right: Basic Info Inputs -->
          <div class="w-full md:w-2/3">
            <vs-row class="mb-5">
              <label>{{ $t('orgDashboard.createOrganization.organizationName') }}</label>
              <vs-input class="w-full mt-1" v-model="localData.name" />
            </vs-row>
            
            <vs-row class="mb-5">
              <label>{{ $t('orgDashboard.createOrganization.organizationUsername') }}</label>
              <vs-input class="w-full mt-1" v-model="localData.slug" @input="checkSlug()" />
              <span v-if="localData.slug" class="mt-1 block">
                <small v-if="usernameError" class="text-danger">{{ usernameError }}</small>
                <small v-else-if="usernameAvailable" class="text-success">{{ usernameAvailable }}</small>
              </span>
            </vs-row>
            
            <vs-row class="mb-5">
              <label>{{ $t('orgDashboard.createOrganization.website') }}</label>
              <vs-input class="w-full mt-1" v-model="localData.website" />
            </vs-row>
            
            <vs-row class="mb-5">
              <label>{{ $t('orgDashboard.createOrganization.location') }}</label>
              <vs-input class="w-full mt-1" v-model="localData.location" />
            </vs-row>
          </div>
        </div>

        <!-- Middle Section: Theme Colors -->
        <div class="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
          
          <div class="w-full md:w-1/2">
            <label>{{ $t('orgDashboard.createOrganization.primaryThemeColor') }}</label>
            <div class="flex flex-wrap gap-2 mt-3">
              <div
                v-for="(cs, index) in colorSet"
                :key="index"
                class="colorset-item flex items-center justify-center relative shadow-sm"
                @click.stop="setPrimaryThemeColor(cs)"
                :style="{ backgroundColor: cs }"
              >
                <feather-icon
                  v-if="localData.primaryThemeColor === cs"
                  icon="CheckIcon"
                  svgClasses="text-white w-5 h-5 drop-shadow-md"
                />
              </div>
            </div>
          </div>

          <div class="w-full md:w-1/2">
            <label>{{ $t('orgDashboard.createOrganization.secondaryThemeColor') }}</label>
            <vx-input-group class="w-full mt-3">
              <template slot="prepend">
                <div class="prepend-text bg-primary">
                  <span>#</span>
                </div>
              </template>
              <vs-input class="w-full" v-model="localData.secondaryThemeColor" />
            </vx-input-group>
          </div>
        </div>

        <!-- Bottom Section: About -->
        <div class="vx-row mb-8">
          <div class="vx-col w-full">
            <vs-row>
              <label class="mb-2 block">{{ $t('orgDashboard.createOrganization.about') }}</label>
              <vs-textarea
                class="w-full"
                counter="200"
                :counter-danger.sync="counterDanger"
                maxlength="200"
                v-model="localData.about"
              />
            </vs-row>
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex flex-wrap items-center justify-end">
          <vs-button
            color="#44CC88"
            class="ml-auto"
            :disabled="!localData.name || !localData.slug"
            @click.stop="saveOrganizationProfile()"
          >
            {{ $t('orgDashboard.createOrganization.saveChanges') }}
          </vs-button>
        </div>

      </div>
    </vx-card>
  </div>
</template>

<script>
import {
  successNotifyService,
  failureNotifyService,
} from "@/services/errors.service.js";
import constants from "@/constants.js";
import vSelect from "vue-select";
import orgDefaultConfig from "@/assets/data/org-default-config.js"

export default {
  data() {
    return {
      counterDanger: false,
      localData: {
      primaryThemeColor: "#3BA0BE",
      secondaryThemeColor: "000000"
    },
      colorSet: [
        "#3BA0BE",
        "#6859B4",
        "#008000",
        "#B3B3B3",
        "#C6211B",
        "#E4D033",
        "#EA7526",
        "#F36677",
      ],
      constants,
      logoInput: null,
      aspectRatio: 1,
      editLogoPrompt: false,
      tempLogo: null,
      initialImgInput: null,
      newLogoUrl: null,
      usernameError: false,
      usernameAvailable: false,
    };
  },
  components: {
    vSelect,
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    userData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    orgData() {
      return this.$store.state.organization.orgData;
    },
  },
  methods: {
    updateOrganizationLogo(input) {
      const payload = {
        imgInput: input,
        _id: this.userId,
        maxSize: 1,
      };

      const _id = payload._id;
      const logoData = input.target.result;
      const logoUrl = _id + "-" + this.initialImgInput.target.files[0].name;

      const olObj = {
        logoData,
        logoUrl,
      };

      this.logoInput = logoData;
      this.localData.olObj = olObj;
      this.localData.logo = logoUrl;
    },
    setPrimaryThemeColor(cs) {
      this.localData["primaryThemeColor"] = cs;
      this.$forceUpdate();
    },
    saveOrganizationProfile() {
      this.$vs.loading();

      const payload = {
        userId: this.userId,
        name: this.localData.name,
        slug:this.localData.slug,
        website: this.localData.website,
        logo: this.localData.logo,
        olObj: this.localData.olObj,
        workEmail: this.localData.workEmail,
        organizationType: this.localData.organizationType,
        location: this.localData.location,
        primaryThemeColor: this.localData.primaryThemeColor,
        secondaryThemeColor: this.localData.secondaryThemeColor,
        organizationCulture: this.localData.organizationCulture,
        about: this.localData.about,
      };

      this.$store
        .dispatch("organization/createOrganization", payload)
        .then((res) => {
          this.$store
          .dispatch("organization/setDefaultOrganizationConfig",orgDefaultConfig(res.data._id))
          .then(res=>{
            this.$vs.loading.close();
            window.location.href = `/organization/${res.data.slug}/home`;
          })
          .catch(err=>{
            window.location.href = `/organization/${res.data.slug}/home`;
            throw err;
          })
        })
        .catch((err) => {
          throw err;
        });
    },
    checkSlug() {
      this.$store
        .dispatch("organization/checkSlug", this.localData.slug)
        .then((res) => {
          if (res.total > 0) {
            this.usernameError = this.$t('orgDashboard.createOrganization.usernameExists');
            this.usernameAvailable = false;
          } else {
            this.usernameError = false;
            this.usernameAvailable = this.$t('orgDashboard.createOrganization.usernameAvailable');
          }
        })
        .catch((err) => {});
    },
    closeEditLogoPrompt() {
      this.$forceUpdate();
    },
    logoValidations(imgInput, maxSize) {
      if (imgInput.target.files && imgInput.target.files[0]) {
        let currentThis = this;

        if (imgInput.target.files > 1) {
          return;
        }
        const parts = imgInput.target.files[0].name.split(".");
        const ext = parts[parts.length - 1];

        if (ext !== "jpg" && ext !== "jpeg" && ext !== "png") {
          failureNotifyService({
            title: this.$t('orgDashboard.createOrganization.logo.incorrectFileType'),
            text: this.$t('orgDashboard.createOrganization.logo.incorrectFileTypeText'),
            currentThis,
          });
          return;
        }

        if (imgInput.target.files[0].size / (1024 * 1024) > maxSize) {
          failureNotifyService({
            title: this.$t('orgDashboard.createOrganization.logo.fileSizeExceeded'),
            text: this.$t('orgDashboard.createOrganization.logo.fileSizeExceededText'),
            currentThis,
          });
          return;
        }
        return true;
      }
    },
    openEditLogoPrompt(input) {
      if (!this.logoValidations(input, 1)) return;

      this.initialImgInput = input;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.tempLogo = e.target.result;
        this.editLogoPrompt = true;
      };
      reader.readAsDataURL(input.target.files[0]);
    },
    cropAndUploadLogo() {
      const currentThis = this;
      const canvas = this.$refs.clipper.clip();
      canvas.toBlob(function (blob) {
        const reader = new FileReader();
        reader.onload = (e) => {
          currentThis.updateOrganizationLogo(e);
        };
        reader.readAsDataURL(blob);
      });
    },
  }
};
</script>

<style scoped>
/* Reset aligns */
* {
  text-align: left;
}
.text-center {
  text-align: center;
}

/* Custom Upload Avatar */
.profile-picture-wrapper {
  position: relative;
  width: 160px;
  height: 160px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
  border: 1px solid #ebedf0;
  background-color: #f8f9fa;
}

.profile-picture {
  position: relative;
  width: 160px !important;
  height: 160px !important;
}

/* Persistent Camera Badge styling */
.camera-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: transform 0.2s, background-color 0.2s;
  z-index: 10;
}

.profile-picture-wrapper:hover .camera-badge {
  background-color: #f3f4f6;
  transform: scale(1.05);
}

.profile-picture-wrapper:hover {
  opacity: 0.9;
}

/* Color Selection Boxes */
.colorset-item {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.colorset-item:hover {
  transform: translateY(-2px);
}

/* Keep Vuesax textarea sizing normal */
::v-deep .vs-textarea {
  background: #fbfbfc;
  border-radius: 6px;
  height: 130px !important;
  color: #888888;
}

::v-deep .vs-input--input {
  background: #fbfbfc !important;
}
</style>